import { useState, useRef } from 'react';
import { useApiData } from '../../../../contexts/apiService'
import Dropdown, { DropdownOption } from '../../../../components/common/dropdown/dropdown';
import { downloadAnalysisAsExcel } from './excel_exporter/ExcelExporter';
import { runRouteAnalysis, AnalysisResult } from './route_analyzer';

interface DataRow {
    leaseName: string;
    coordinates: string;
    estGravity: string;
    bpd: string;
    bestMarket?: string;
    secBestMarket?: string;
}

interface MapMarkerData {
    lease: string;
    lat: number;
    lng: number;
    producer: string;
    api: number | null;
    bpd: number | null;
    color: string;
    key: string;
    isManualInput?: boolean;
}

interface DataInputProps {
    onAddToMap?: (data: MapMarkerData[]) => void;
}

export default function DataInput({ onAddToMap }: DataInputProps) {
    
    const { apiData } = useApiData();
    const [rows, setRows] = useState<DataRow[]>([{ leaseName: '', coordinates: '', estGravity: '', bpd: '' }]);
    const [selectedCarrier, setSelectedCarrier] = useState<string>('south_haven');
    const [selectedMarket, setSelectedMarket] = useState<string>('');
    const [purchaseDeductInput, setPurchaseDeductInput] = useState<string>('');
    const [saleDeductInput, setSaleDeductInput] = useState<string>('');
    const purchaseDeduct = purchaseDeductInput === '' ? 0 : Number(purchaseDeductInput);
    const [lastAnalysisResults, setLastAnalysisResults] = useState<AnalysisResult[]>([]);

    const cellRefs = useRef<{ [key: string]: (HTMLInputElement | null)[] }>({
        leaseName: [],
        coordinates: [],
        estGravity: [],
        bpd: []
    });

    // Get available carriers from trucking_rates
    const getAvailableCarriers = (): DropdownOption[] => {
        if (!apiData?.trucking_rates || apiData.trucking_rates.length === 0) {
            return [];
        }
        
        const firstRow = apiData.trucking_rates[0];
        const carriers = Object.keys(firstRow).filter(key => key !== 'id' && key !== 'mileage' && key !== 'uuid');
        
        return carriers.map(carrier => ({
            value: carrier,
            label: carrier.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        }));
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, column: keyof DataRow, index: number): void => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        const lines = pastedText.split('\n').filter(line => line.trim() !== '');
        
        if (lines.length > 200) {
            lines.splice(200);
        }
    
        const newRows = [...rows];
        
        while (newRows.length < index + lines.length) {
            newRows.push({ leaseName: '', coordinates: '', estGravity: '', bpd: '' });
        }
        
        // Helper function to clean pasted values
        const cleanPastedValue = (value: string): string => {
            let cleaned = value.trim();
            
            // Remove surrounding quotes if they exist
            if ((cleaned.startsWith('"') && cleaned.endsWith('"')) || 
                (cleaned.startsWith("'") && cleaned.endsWith("'"))) {
                cleaned = cleaned.slice(1, -1);
            }
            
            // Remove any remaining quotes that might be in the middle
            // (but be careful not to remove quotes that are part of actual content)
            if (column === 'coordinates') {
                // For coordinates, remove all quotes since they shouldn't contain any
                cleaned = cleaned.replace(/['"]/g, '');
            }
            
            return cleaned.trim();
        };
        
        for (let i = 0; i < lines.length; i++) {
            const rowIndex = index + i;
            const cleanedValue = cleanPastedValue(lines[i]);
            newRows[rowIndex][column] = cleanedValue;
        }
        
        setRows(newRows);
    };

    const handleChange = (column: keyof DataRow, index: number, value: string): void => {
        const newRows = [...rows];
        newRows[index][column] = value;
        setRows(newRows);
    };

    const addRow = (): void => {
        setRows([...rows, { leaseName: '', coordinates: '', estGravity: '', bpd: '' }]);
    };

    const clearAll = (): void => {
        setRows([{ leaseName: '', coordinates: '', estGravity: '', bpd: '' }]);
    };

    // Parse coordinates string (supports various formats)
    const parseCoordinates = (coordString: string) => {
        if (!coordString || typeof coordString !== 'string') return null;
        
        const cleaned = coordString.trim();
        
        const commaMatch = cleaned.match(/^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/);
        if (commaMatch) {
            return {
                lat: parseFloat(commaMatch[1]),
                lng: parseFloat(commaMatch[2])
            };
        }
        
        const spaceMatch = cleaned.match(/^(-?\d+\.?\d*)\s+(-?\d+\.?\d*)$/);
        if (spaceMatch) {
            return {
                lat: parseFloat(spaceMatch[1]),
                lng: parseFloat(spaceMatch[2])
            };
        }
        
        return null;
    };

    // Validate if coordinates are within reasonable bounds
    const isValidCoordinate = (lat: number, lng: number) => {
        return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
    };

    const addToMap = (): void => {
        const validRows = rows.filter(row => {
            const hasLeaseName = row.leaseName.trim() !== '';
            const hasCoordinates = row.coordinates.trim() !== '';
            return hasLeaseName && hasCoordinates;
        });

        if (validRows.length === 0) {
            alert('Please add at least one row with lease name and coordinates before adding to map.');
            return;
        }

        const mapData: MapMarkerData[] = [];
        const errors: string[] = [];

        validRows.forEach((row, index) => {
            const coords = parseCoordinates(row.coordinates);
            
            if (!coords) {
                errors.push(`Row ${index + 1}: Invalid coordinate format "${row.coordinates}". Use format: "lat, lng"`);
                return;
            }

            if (!isValidCoordinate(coords.lat, coords.lng)) {
                errors.push(`Row ${index + 1}: Coordinates out of valid range. Latitude: ${coords.lat}, Longitude: ${coords.lng}`);
                return;
            }

            const timestamp = Date.now();
            const randomId = Math.random().toString(36).substr(2, 9);
            mapData.push({
                lease: row.leaseName,
                lat: coords.lat,
                lng: coords.lng,
                producer: 'Manual Input',
                api: row.estGravity ? parseFloat(row.estGravity) : null,
                bpd: row.bpd ? parseFloat(row.bpd) : null,
                color: '#FF6B35',
                key: `manual-${row.leaseName}-${coords.lat}-${coords.lng}-${timestamp}-${randomId}`,
                isManualInput: true
            });
        });

        if (errors.length > 0) {
            alert('Errors found:\n' + errors.join('\n'));
            return;
        }

        if (onAddToMap) {
            onAddToMap(mapData);
        }

        alert(`Successfully added ${mapData.length} point(s) to the map!`);
    };

    // Main analysis function
    const onBatchAnalysis = async (): Promise<void> => {
        try {
            if (!apiData) {
                alert('API data is not loaded yet. Please wait for data to load before analyzing routes.');
                return;
            }
            
            // Parse sale deduct input
            const saleDeduct = saleDeductInput === '' ? undefined : Number(saleDeductInput);
            // Validate sale deduct usage
            if (saleDeduct !== undefined && !selectedMarket) {
                alert('Sale Deduct can only be used when a specific market is selected. Please select a market from the dropdown.');
                return;
            }
                
            const { analysisResults, errors } = await runRouteAnalysis({
                rows,
                selectedCarrier,
                purchaseDeduct,
                apiData,
                specificMarket: selectedMarket || undefined,
                saleDeduct: saleDeduct
            });
            
            setLastAnalysisResults(analysisResults);
            updateRowsWithResults(analysisResults);
            showAnalysisResults(analysisResults, errors);
            
        } catch (error) {
            console.error('Analysis error:', error);
            alert(error instanceof Error ? error.message : 'An error occurred during analysis');
        }
    };

    const updateRowsWithResults = (analysisResults: AnalysisResult[]) => {
        const newRows = [...rows];
        analysisResults.forEach(result => {
            const originalRowIndex = rows.findIndex(r => 
                r.leaseName === result.leaseName && r.coordinates === result.coordinates
            );
            
            if (originalRowIndex !== -1) {
                if (result.error || result.allOptions.length === 0) {
                    newRows[originalRowIndex].bestMarket = `ERROR: Could not calculate route`;
                    newRows[originalRowIndex].secBestMarket = 'No options available';
                } else {
                    // Best option (first in allOptions)
                    const best = result.allOptions[0];
                    const rateText = best.truckingRate ? ` - rate $${best.truckingRate}` : '';
                    const discountText = best.marketDiscount ? ` + $${best.marketDiscount} discount` : '';
                    const netValueText = best.netValue !== null ? ` = $${best.netValue.toFixed(2)} net` : '';
                    const marginText = best.margin !== null ? ` (Margin: $${best.margin.toFixed(2)})` : '';
                    const truckAmount = result.trucksPerMonth ? ` - Loads p/month ${result.trucksPerMonth}` : '';
                    const perMonthTotal = result.trucksPerMonth && best.margin !== null ? 
                        ` - Total p/month $${(result.trucksPerMonth * best.margin * 170).toFixed(2)}` : '';
    
                    newRows[originalRowIndex].bestMarket = 
                        `${best.displayName} - ${best.distance} mi${rateText}${discountText}${netValueText}${marginText}${truckAmount}${perMonthTotal}`;
                    
                    // Second best option (if available)
                    if (result.allOptions.length > 1) {
                        const second = result.allOptions[1];
                        const secondRateText = second.truckingRate ? ` - rate $${second.truckingRate}` : '';
                        const secondDiscountText = second.marketDiscount ? ` + $${second.marketDiscount} discount` : '';
                        const secondNetValueText = second.netValue !== null ? ` = $${second.netValue.toFixed(2)} net` : '';
                        const secondMarginText = second.margin !== null ? ` (Margin: $${second.margin.toFixed(2)})` : '';
                        const secondTruckAmount = result.trucksPerMonth ? ` - Loads p/month ${result.trucksPerMonth}` : '';
                        const secondPerMonthTotal = result.trucksPerMonth && second.margin !== null ? 
                            ` - Total p/month $${(result.trucksPerMonth * second.margin * 170).toFixed(2)}` : '';
    
                        newRows[originalRowIndex].secBestMarket = 
                            `${second.displayName} - ${second.distance} mi${secondRateText}${secondDiscountText}${secondNetValueText}${secondMarginText}${secondTruckAmount}${secondPerMonthTotal}`;
                    } else {
                        newRows[originalRowIndex].secBestMarket = 'No second option available';
                    }
                }
            }
        });
        setRows(newRows);
    };
    
    const showAnalysisResults = (analysisResults: AnalysisResult[], errors: string[]) => {
        if (errors.length > 0) {
            console.error('Analysis errors:', errors);
            alert('Some errors occurred during analysis. Check console for details.');
        }
    
        if (analysisResults.length > 0) {
            alert(`Route Analysis Complete!`);
            console.log('Detailed Analysis Results:', analysisResults);
        }
    };

    const handleDownloadExcel = (): void => {
        if (lastAnalysisResults.length === 0) {
            alert('No analysis results to download. Please run "Analyze All Routes" first.');
            return;
        }
        
        downloadAnalysisAsExcel(lastAnalysisResults, rows, purchaseDeduct);
    };

    return (
        <div className="di-main-container">
            <div className="flex gap-2 mb-4 p-2">
                <button 
                    onClick={addRow}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                >
                    Add Row
                </button>
                <button 
                    onClick={clearAll}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                >
                    Clear All
                </button>
                <button 
                    onClick={addToMap}
                    className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                >
                    Add to Map
                </button>
                <button 
                    onClick={onBatchAnalysis}
                    className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
                >
                    Analyze All Routes
                </button>
                <button 
                    onClick={handleDownloadExcel}
                    className="px-4 py-2 bg-emerald-500 text-white rounded hover:bg-emerald-600 transition-colors"
                    title="Download analysis results as Excel file"
                >
                    📊 Download Excel
                </button>
            </div>

            <div className="flex gap-4 mb-4">
                <div className="flex-1">
                    <Dropdown
                        options={getAvailableCarriers()}
                        value={selectedCarrier}
                        onChange={(carrier) => setSelectedCarrier(carrier)}
                        placeholder="Select carrier for analysis..."
                        isDisabled={getAvailableCarriers().length === 0}
                    />
                    <Dropdown
                        tableData={apiData?.market_discounts}
                        columnName={"station"}
                        value={selectedMarket}
                        onChange={(carrier) => setSelectedMarket(carrier)}
                        placeholder="Select station"
                    />
                </div>
                <div className="flex-shrink-0">
                    <label className="block font-semibold mb-2 text-sm">Purchase Deduct ($)</label>
                    <input
                        type="number"
                        value={purchaseDeductInput}
                        onChange={(e) => setPurchaseDeductInput(e.target.value)}
                        className="w-32 h-10 border border-gray-300 bg-white px-2 text-sm rounded"
                        placeholder="0.00"
                        step="0.1"
                    />
                    <label className="block font-semibold mb-2 text-sm">Sale Deduct ($)</label>
                    <input
                        type="number"
                        value={saleDeductInput}
                        onChange={(e) => setSaleDeductInput(e.target.value)}
                        className="w-32 h-10 border border-gray-300 bg-white px-2 text-sm rounded"
                        placeholder="0.00"
                        step="0.1"
                    />
                </div>
            </div>
            
            <div className="di-data-container">
                <div className="di-columns-wrapper">
                    {/* Lease Name Column */}
                    <div className="di-column">
                        <label className="block font-semibold mb-2 text-sm">Lease Name</label>
                        {rows.map((row, index) => (
                            <input
                                key={`leaseName-${index}`}
                                ref={el => cellRefs.current.leaseName[index] = el}
                                type="text"
                                value={row.leaseName}
                                onChange={(e) => handleChange('leaseName', index, e.target.value)}
                                onPaste={(e) => handlePaste(e, 'leaseName', index)}
                                className="w-full h-8 border border-gray-300 bg-white mb-1 px-2 text-sm"
                                placeholder="Lease name"
                            />
                        ))}
                    </div>

                    {/* Coordinates Column */}
                    <div className="di-column">
                        <label className="block font-semibold mb-2 text-sm">Coordinates</label>
                        {rows.map((row, index) => (
                            <input
                                key={`coordinates-${index}`}
                                ref={el => cellRefs.current.coordinates[index] = el}
                                type="text"
                                value={row.coordinates}
                                onChange={(e) => handleChange('coordinates', index, e.target.value)}
                                onPaste={(e) => handlePaste(e, 'coordinates', index)}
                                className="w-full h-8 border border-gray-300 bg-white mb-1 px-2 text-sm"
                                placeholder="lat, long"
                            />
                        ))}
                    </div>

                    {/* Est Gravity Column */}
                    <div className="di-column">
                        <label className="block font-semibold mb-2 text-sm">Est Gravity</label>
                        {rows.map((row, index) => (
                            <input
                                key={`estGravity-${index}`}
                                ref={el => cellRefs.current.estGravity[index] = el}
                                type="text"
                                value={row.estGravity}
                                onChange={(e) => handleChange('estGravity', index, e.target.value)}
                                onPaste={(e) => handlePaste(e, 'estGravity', index)}
                                className="w-full h-8 border border-gray-300 bg-white mb-1 px-2 text-sm"
                                placeholder="API gravity"
                            />
                        ))}
                    </div>

                    {/* BPD Column */}
                    <div className="di-column">
                        <label className="block font-semibold mb-2 text-sm">BPD</label>
                        {rows.map((row, index) => (
                            <input
                                key={`bpd-${index}`}
                                ref={el => cellRefs.current.bpd[index] = el}
                                type="text"
                                value={row.bpd}
                                onChange={(e) => handleChange('bpd', index, e.target.value)}
                                onPaste={(e) => handlePaste(e, 'bpd', index)}
                                className="w-full h-8 border border-gray-300 bg-white mb-1 px-2 text-sm"
                                placeholder="BPD"
                            />
                        ))}
                    </div>

                    {/* Best Market Column */}
                    <div className="di-column">
                        <label className="block font-semibold mb-2 text-sm">Best Market</label>
                        {rows.map((row, index) => (
                            <div
                                key={`bestMarket-${index}`}
                                className="w-full h-8 border border-gray-300 bg-gray-50 mb-1 px-2 text-sm flex items-center"
                            >
                                <span className="text-gray-700 text-xs">
                                    {row.bestMarket || 'Click "Analyze All Routes" to find closest market'}
                                </span>
                            </div>
                        ))}
                    </div>

                    {/* Second Best Market Column */}
                    <div className="di-column">
                        <label className="block font-semibold mb-2 text-sm">2nd Best Market</label>
                        {rows.map((row, index) => (
                            <div
                                key={`secBestMarket-${index}`}
                                className="w-full h-8 border border-gray-300 bg-gray-50 mb-1 px-2 text-sm flex items-center"
                            >
                                <span className="text-gray-700 text-xs">
                                    {row.secBestMarket || 'Click "Analyze All Routes" to find closest market'}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}